/**
 * Violin plot image utilities
 * - categorizeViolinPlots: single source of truth for grouping plots into
 *   input/time/output sections and resolving display labels (shared by the
 *   on-screen grid and the combined-image download)
 * - composeViolinPlotsImage: draws all plots onto one canvas and returns a PNG blob
 */

import { ViolinPlotResponse } from '../types';

export interface CategorizedPlot {
  name: string;
  src: string;
  label: string;
}

export interface CategorizedPlots {
  inputPlots: CategorizedPlot[];
  timePlots: CategorizedPlot[];
  outputPlots: CategorizedPlot[];
}

export const toImageSrc = (plotData: string): string =>
  plotData.startsWith('data:') ? plotData : `data:image/png;base64,${plotData}`;

export const categorizeViolinPlots = (violinPlots: ViolinPlotResponse): CategorizedPlots => {
  const inputPlots: CategorizedPlot[] = [];
  const timePlots: CategorizedPlot[] = [];
  const outputPlots: CategorizedPlot[] = [];
  const rawLabels = new Map<string, string | undefined>();

  Object.entries(violinPlots.plots).forEach(([plotName, plotInfo]) => {
    if (!plotInfo) return;

    // Handle both new format (object with data and type) and legacy format (string)
    const isNewFormat = typeof plotInfo === 'object' && 'data' in plotInfo;
    const plotData = isNewFormat ? plotInfo.data : plotInfo;
    const plotType = isNewFormat ? plotInfo.type : 'input';
    rawLabels.set(plotName, isNewFormat ? plotInfo.label : undefined);

    if (typeof plotData !== 'string') return;
    const entry: CategorizedPlot = { name: plotName, src: toImageSrc(plotData), label: '' };

    if (plotType === 'time') {
      timePlots.push(entry);
    } else if (plotType === 'output') {
      outputPlots.push(entry);
    } else {
      inputPlots.push(entry);
    }
  });

  // Fallback labels: time plots continue the input numbering (IN3, IN4, ...)
  inputPlots.forEach((p, i) => { p.label = rawLabels.get(p.name) || `IN${i + 1}`; });
  timePlots.forEach((p, i) => { p.label = rawLabels.get(p.name) || `IN${inputPlots.length + i + 1}`; });
  outputPlots.forEach((p, i) => { p.label = rawLabels.get(p.name) || `OUT${i + 1}`; });

  return { inputPlots, timePlots, outputPlots };
};

export interface ComposeSection {
  title: string;
  plots: CategorizedPlot[];
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image failed to load'));
    img.src = src;
  });

const CELL_WIDTH = 560;
const GAP = 24;
const MARGIN = 40;
const SECTION_TITLE_HEIGHT = 48;
const SECTION_SPACING = 32;
const LABEL_HEIGHT = 34;
const FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

/**
 * Compose all violin plots into a single PNG image, grouped in sections
 * that mirror the on-screen layout. Plots whose image data fails to load
 * are skipped; throws only if no plot could be rendered at all.
 */
export const composeViolinPlotsImage = async (sections: ComposeSection[]): Promise<Blob> => {
  const loadedSections = await Promise.all(
    sections.map(async (section) => {
      const images = await Promise.all(
        section.plots.map(async (plot) => {
          try {
            return { img: await loadImage(plot.src), label: plot.label };
          } catch {
            return null;
          }
        })
      );
      return {
        title: section.title,
        images: images.filter((i): i is { img: HTMLImageElement; label: string } => i !== null)
      };
    })
  );

  const drawableSections = loadedSections.filter((s) => s.images.length > 0);
  if (drawableSections.length === 0) {
    throw new Error('No violin plot images could be loaded');
  }

  const cols = Math.min(3, Math.max(...drawableSections.map((s) => s.images.length)));
  const canvasWidth = MARGIN * 2 + cols * CELL_WIDTH + (cols - 1) * GAP;

  // Pre-compute row layout: each row's height is its tallest scaled image + label
  const sectionLayouts = drawableSections.map((section) => {
    const rows: { img: HTMLImageElement; label: string; height: number }[][] = [];
    for (let i = 0; i < section.images.length; i += cols) {
      const row = section.images.slice(i, i + cols).map((item) => ({
        ...item,
        height: (item.img.naturalHeight / item.img.naturalWidth) * CELL_WIDTH
      }));
      rows.push(row);
    }
    const height = SECTION_TITLE_HEIGHT + rows.reduce(
      (sum, row) => sum + Math.max(...row.map((r) => r.height)) + LABEL_HEIGHT + GAP,
      0
    );
    return { title: section.title, rows, height };
  });

  const canvasHeight = MARGIN * 2 +
    sectionLayouts.reduce((sum, s) => sum + s.height, 0) +
    (sectionLayouts.length - 1) * SECTION_SPACING;

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context not available');
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  ctx.textAlign = 'center';

  let y = MARGIN;
  sectionLayouts.forEach((section) => {
    ctx.fillStyle = '#6b7280';
    ctx.font = `600 18px ${FONT_FAMILY}`;
    ctx.fillText(section.title.toUpperCase(), canvasWidth / 2, y + 22);
    y += SECTION_TITLE_HEIGHT;

    section.rows.forEach((row) => {
      const rowHeight = Math.max(...row.map((r) => r.height));
      const rowWidth = row.length * CELL_WIDTH + (row.length - 1) * GAP;
      let x = (canvasWidth - rowWidth) / 2;

      row.forEach((item) => {
        // Vertically center shorter images within the row
        const offsetY = (rowHeight - item.height) / 2;
        ctx.drawImage(item.img, x, y + offsetY, CELL_WIDTH, item.height);
        ctx.fillStyle = '#111827';
        ctx.font = `500 15px ${FONT_FAMILY}`;
        ctx.fillText(item.label, x + CELL_WIDTH / 2, y + rowHeight + 22);
        x += CELL_WIDTH + GAP;
      });

      y += rowHeight + LABEL_HEIGHT + GAP;
    });

    y += SECTION_SPACING;
  });

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Canvas export failed'));
      }
    }, 'image/png');
  });
};
